import React, { useEffect, useState } from "react";
import {
    Settings as SettingsIcon, User, Bell, CreditCard, Link as LinkIcon, Lock,
    ChevronDown,
} from "lucide-react";
import AppShell from "@/components/nav/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import ProfileSection from "@/components/settings/ProfileSection";
import BillingSection from "@/components/settings/BillingSection";
import GoogleAdsSection from "@/components/settings/GoogleAdsSection";
import NotificationsSection from "@/components/settings/NotificationsSection";
import SecuritySection from "@/components/settings/SecuritySection";

/**
 * Settings — self-service hub for account management.
 *
 * PR #9:  Profile, Billing, Google Ads — fully functional.
 * PR #12 (current): Notifications matrix.
 * PR #13 (next):    Security / Delete account.
 *
 * Sections rendered as a collapsible accordion (first open by default).
 * Locked sections (PR #10+) display a "Скоро" badge and are non-clickable.
 */

const SectionHeader = ({ icon: Icon, title, sub, badge, open, onClick, disabled, testid }) => (
    <button
        type="button"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={`w-full flex items-center gap-4 p-5 text-left transition-colors
                    ${disabled
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:bg-surface-hover/30 cursor-pointer"}`}
        data-testid={testid}
        aria-expanded={open}
    >
        <div className="w-10 h-10 rounded-lg bg-surface-hover/40 flex items-center justify-center shrink-0">
            <Icon size={18} className="text-secondary-c" />
        </div>
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-semibold text-base text-text-primary">{title}</h3>
                {badge && (
                    <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full
                                     bg-surface-hover/40 text-faint-c border border-border">
                        {badge}
                    </span>
                )}
            </div>
            <p className="mt-0.5 text-xs text-secondary-c">{sub}</p>
        </div>
        {!disabled && (
            <ChevronDown
                size={18}
                className={`text-faint-c shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            />
        )}
    </button>
);

const Section = ({ icon, title, sub, badge, disabled, open, onToggle, testid, children }) => (
    <div
        className={`glass rounded-xl border border-border overflow-hidden transition-shadow
                    ${open ? "shadow-lg shadow-primary/5" : ""}`}
        data-testid={`settings-section-${testid}`}
    >
        <SectionHeader
            icon={icon}
            title={title}
            sub={sub}
            badge={badge}
            open={open}
            onClick={onToggle}
            disabled={disabled}
            testid={`settings-section-${testid}-toggle`}
        />
        {open && !disabled && (
            <div className="px-5 pb-5 pt-1 border-t border-border/40">
                {children}
            </div>
        )}
    </div>
);

const Settings = () => {
    const { user, refresh } = useAuth();
    const [openSection, setOpenSection] = useState("profile");

    useEffect(() => {
        const onRefresh = () => refresh?.();
        window.addEventListener("user:refresh", onRefresh);
        return () => window.removeEventListener("user:refresh", onRefresh);
    }, [refresh]);

    const toggle = (key) => setOpenSection(prev => prev === key ? null : key);

    return (
        <AppShell testid="settings-page">
            <div className="max-w-3xl">
                <div className="flex items-center gap-3">
                    <SettingsIcon size={22} className="text-primary-c" />
                    <div className="text-xs uppercase tracking-wider font-mono text-primary-c">Налаштування</div>
                </div>
                <h1 className="mt-2 font-display font-extrabold text-text-primary"
                    style={{ fontSize: "clamp(32px, 4.5vw, 48px)", letterSpacing: "-0.02em" }}>
                    Налаштування акаунта
                </h1>
                <p className="mt-3 text-secondary-c max-w-xl">
                    Керуйте профілем, підпискою, доступом до Google Ads та сповіщеннями. Безпека акаунту — наступним апдейтом.
                </p>

                <div className="mt-10 space-y-3">
                    <Section
                        icon={User}
                        title="Профіль"
                        sub="Імʼя, email, дані бізнесу."
                        testid="profile"
                        open={openSection === "profile"}
                        onToggle={() => toggle("profile")}
                    >
                        <ProfileSection user={user} />
                    </Section>

                    <Section
                        icon={CreditCard}
                        title="Тарифи та білінг"
                        sub="Поточний план, ліміти, керування підпискою."
                        testid="billing"
                        open={openSection === "billing"}
                        onToggle={() => toggle("billing")}
                    >
                        <BillingSection user={user} />
                    </Section>

                    <Section
                        icon={LinkIcon}
                        title="Google Ads"
                        sub="Стан підключення, переключення акаунту, відключення."
                        testid="googleads"
                        open={openSection === "googleads"}
                        onToggle={() => toggle("googleads")}
                    >
                        <GoogleAdsSection />
                    </Section>

                    <Section
                        icon={Bell}
                        title="Сповіщення"
                        sub="Матриця подій × каналів (in-app, email, push, Telegram)."
                        testid="notifications"
                        open={openSection === "notifications"}
                        onToggle={() => toggle("notifications")}
                    >
                        <NotificationsSection />
                    </Section>

                    <Section
                        icon={Lock}
                        title="Безпека"
                        sub="Активні сесії та видалення акаунту."
                        testid="security"
                        open={openSection === "security"}
                        onToggle={() => toggle("security")}
                    >
                        <SecuritySection />
                    </Section>
                </div>


            </div>
        </AppShell>
    );
};

export default Settings;